import { useState, useEffect } from 'react'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FlashCropValidator } from '@/components/admin/FlashCropValidator'
import { uploadFlashImage, createFotwFlash } from '@/lib/portfolioUpload'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { X, Zap, Euro, Ruler, AlertCircle, Upload, Loader2, CheckCircle } from 'lucide-react'
import type { Artist } from '@/types/models'
import type { FlashCropAnalysis } from '@/lib/flashCropValidation'

interface FotwFlash {
  id: string
  imageUrl: string // Firebase Storage URL
  artistId: string
  minSize: string
  filename: string
  cropValidation?: {
    isValid: boolean
    confidence: number
    analyzed: boolean
    error?: string
  }
}

export default function FotwUploadsPage() {
  const [artists, setArtists] = useState<Artist[]>([])
  const [fotwWeek, setFotwWeek] = useState<string>('')
  const [minPrice, setMinPrice] = useState<string>('')
  const [flashes, setFlashes] = useState<FotwFlash[]>([])
  const [uploadingFlashes, setUploadingFlashes] = useState<Set<string>>(new Set())
  const [loadingArtists, setLoadingArtists] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Fetch all artists
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        setLoadingArtists(true)
        const querySnapshot = await getDocs(collection(db, 'artists'))
        const allArtists = querySnapshot.docs
          .map(doc => {
            const data = doc.data()
            return {
              id: doc.id,
              displayName: data.displayName || 'Unknown Artist',
              firstName: data.firstName,
              lastName: data.lastName,
              location: data.location,
              isAvailable: data.isAvailable,
              bio: data.bio,
              styles: data.styles || [],
              createdAt: data.createdAt,
              avatarUrl: data.avatarUrl,
            } as Artist
          })
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
        
        console.log(`[FotwUploads] Loaded ${allArtists.length} available artists`)
        setArtists(allArtists)
      } catch (error) {
        console.error('Error fetching artists:', error)
        setErrorMessage('Failed to load artists. Please refresh the page.')
      } finally {
        setLoadingArtists(false)
      }
    }

    fetchArtists()
  }, [])

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    
    for (const file of files) {
      const flashId = `flash_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Add placeholder flash immediately
      const placeholderFlash: FotwFlash = {
        id: flashId,
        imageUrl: '', // Will be filled after upload
        artistId: '',
        minSize: '',
        filename: file.name,
        cropValidation: {
          isValid: false,
          confidence: 0,
          analyzed: false
        }
      }
      setFlashes(prev => [...prev, placeholderFlash])
      setUploadingFlashes(prev => new Set([...prev, flashId]))
      
      try {
        // Upload to Firebase Storage immediately
        const imageUrl = await uploadFlashImage(file, 'temp')
        
        // Update with Firebase URL
        setFlashes(prev => prev.map(flash => 
          flash.id === flashId ? { ...flash, imageUrl } : flash
        ))
      } catch (error) {
        console.error('Failed to upload image:', error)
        setErrorMessage(`Failed to upload ${file.name}. Please try again.`)
        // Remove failed flash
        setFlashes(prev => prev.filter(f => f.id !== flashId))
      } finally {
        setUploadingFlashes(prev => {
          const next = new Set(prev)
          next.delete(flashId)
          return next
        })
      }
    }
  }

  const updateFlash = (flashId: string, updates: Partial<FotwFlash>) => {
    setFlashes(prev => prev.map(flash => 
      flash.id === flashId ? { ...flash, ...updates } : flash
    ))
  }

  const removeFlash = (flashId: string) => {
    setFlashes(prev => prev.filter(flash => flash.id !== flashId))
  }

  const handleCropValidation = (flashId: string, isValid: boolean, analysis?: FlashCropAnalysis, croppedImageUrl?: string) => {
    const updates: Partial<FotwFlash> = {
      cropValidation: {
        isValid,
        confidence: analysis?.confidence || 0,
        analyzed: true,
        ...(analysis ? {} : { error: 'Analysis failed' })
      }
    }
    
    // If the image was cropped, update the imageUrl
    if (croppedImageUrl) {
      updates.imageUrl = croppedImageUrl
    }
    
    updateFlash(flashId, updates)
  }

  const isFlashComplete = (flash: FotwFlash) => {
    const hasCropValidation = flash.cropValidation?.analyzed && flash.cropValidation?.isValid
    return hasCropValidation
  }

  const canUploadFlashes = () => {
    if (!fotwWeek || !minPrice) return false
    const weekNumber = parseInt(fotwWeek)
    if (isNaN(weekNumber) || weekNumber < 1) return false
    if (flashes.length === 0) return false
    return flashes.every(isFlashComplete)
  }

  const handleUploadAllFlashes = async () => {
    if (!canUploadFlashes() || uploading) return
    
    setErrorMessage(null)
    setSuccessMessage(null)
    setUploading(true)
    
    try {
      const weekNumber = parseInt(fotwWeek)
      let successCount = 0
      let failCount = 0
      
      for (const flash of flashes) {
        try {
          // Images are already uploaded to Firebase Storage with URLs
          // Just create the flash document with the existing URL
          await createFotwFlash(
            flash.artistId,
            flash.imageUrl,
            minPrice,
            flash.minSize,
            weekNumber
          )
          successCount++
        } catch (error) {
          console.error(`Failed to create flash ${flash.id}:`, error)
          failCount++
        }
      }
      
      if (successCount > 0) {
        setSuccessMessage(`Successfully uploaded ${successCount} flash${successCount > 1 ? 'es' : ''} to Flash of the Week ${weekNumber}!`)
        // Clear flashes after successful upload
        setFlashes([])
        setFotwWeek('')
        setMinPrice('')
      }
      
      if (failCount > 0) {
        setErrorMessage(`Failed to upload ${failCount} flash${failCount > 1 ? 'es' : ''}. Please try again.`)
      }
    } catch (error) {
      console.error('Error uploading flashes:', error)
      setErrorMessage('Failed to upload flashes. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const completedFlashes = flashes.filter(isFlashComplete)
  const allFlashesValid = flashes.length > 0 && flashes.every(flash => 
    flash.cropValidation?.analyzed && flash.cropValidation?.isValid
  )

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-background pb-20">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Flash of the Week Uploads</h1>
            <p className="text-muted-foreground">
              Upload flash designs for Flash of the Week contests
            </p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <Card className="mb-6 border-green-500 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800">
                    {successMessage}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {errorMessage && (
            <Card className="mb-6 border-red-500 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800">
                    {errorMessage}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Configuration Card */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>FOTW Configuration</CardTitle>
              <CardDescription>
                Set the week number and minimum price that applies to all flashes in this competition
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Week Number */}
                <div className="space-y-2">
                  <Label htmlFor="week-number">FOTW Week Number</Label>
                  <Input
                    id="week-number"
                    type="number"
                    min="1"
                    value={fotwWeek}
                    onChange={(e) => setFotwWeek(e.target.value)}
                    placeholder="e.g., 1"
                  />
                </div>

                {/* Min Price (same for all) */}
                <div className="space-y-2">
                  <Label htmlFor="min-price" className="flex items-center gap-1">
                    <Euro className="w-3 h-3" />
                    Minimum Price (all flashes)
                  </Label>
                  <Input
                    id="min-price"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="e.g., 150€"
                  />
                </div>
              </div>

              {/* Guidelines */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">FOTW Flash Guidelines</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• One flash per artist per week - upload flashes from different artists</li>
                      <li>• Clean backgrounds - white or very minimal backgrounds work best</li>
                      <li>• Minimum 12% padding on all sides - zoom out rather than too close</li>
                      <li>• No watermarks - flashes are always shown next to the artist's username</li>
                      <li>• Artist and size can be set now or added later in Firestore</li>
                      <li>• All flashes share the same price but sizes vary per design</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Flash Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Upload Flashes
              </CardTitle>
              <CardDescription>
                Upload flash designs with automatic crop validation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Statistics */}
              {flashes.length > 0 && (
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {flashes.length} flash{flashes.length !== 1 ? 'es' : ''} uploaded
                  </Badge>
                  <Badge variant={completedFlashes.length === flashes.length ? "default" : "secondary"}>
                    {completedFlashes.length} completed
                  </Badge>
                  {!allFlashesValid && (
                    <Badge variant="destructive">
                      {flashes.filter(f => !f.cropValidation?.isValid).length} need better crop
                    </Badge>
                  )}
                </div>
              )}

              {/* Flash Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Upload Area Card */}
                <Card className="border-2 border-dashed border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10 transition-colors">
                  <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      id="flash-upload"
                    />
                    <label
                      htmlFor="flash-upload"
                      className="cursor-pointer flex flex-col items-center gap-3 text-center"
                    >
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <Upload className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg mb-1">
                          Upload Flashes
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Select multiple images
                        </p>
                      </div>
                    </label>
                  </CardContent>
                </Card>

                {/* Flash Cards */}
                {flashes.map((flash) => (
                  <Card 
                    key={flash.id} 
                    className={`relative transition-all ${
                      isFlashComplete(flash) ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'
                    }`}
                  >
                    {/* Upload indicator */}
                    {uploadingFlashes.has(flash.id) && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-10">
                        <div className="bg-white rounded-lg p-3 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Uploading to storage...</span>
                        </div>
                      </div>
                    )}
                    
                    <CardContent className="p-4 space-y-3">
                      {/* Image with Crop Validation */}
                      {flash.imageUrl && !uploadingFlashes.has(flash.id) ? (
                        <FlashCropValidator
                          imageUrl={flash.imageUrl}
                          onValidationComplete={(isValid, analysis, croppedImageUrl) => 
                            handleCropValidation(flash.id, isValid, analysis, croppedImageUrl)
                          }
                          className="w-full"
                          autoAnalyze={true}
                          onRemove={() => removeFlash(flash.id)}
                          displayName={flash.artistId || 'temp'}
                          flashId={flash.id}
                        />
                      ) : !uploadingFlashes.has(flash.id) && (
                        <div className="w-full aspect-square rounded-lg overflow-hidden border bg-muted flex items-center justify-center">
                          <p className="text-sm text-muted-foreground">Loading...</p>
                        </div>
                      )}

                      {/* Filename */}
                      {flash.filename && (
                        <p className="text-xs text-muted-foreground truncate">
                          {flash.filename}
                        </p>
                      )}

                      {/* Status Badge */}
                      <div className="flex items-center justify-between">
                        <Badge 
                          variant={isFlashComplete(flash) ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {isFlashComplete(flash) ? "Ready" : "Validating..."}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFlash(flash.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Artist Selection and Size (Optional) */}
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Artist (optional - can be set later)
                          </Label>
                          {loadingArtists ? (
                            <div className="flex items-center gap-2 p-2 border rounded-md">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span className="text-xs text-muted-foreground">Loading...</span>
                            </div>
                          ) : (
                            <Select 
                              value={flash.artistId} 
                              onValueChange={(value) => updateFlash(flash.id, { artistId: value })}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="No artist (set later)" />
                              </SelectTrigger>
                              <SelectContent>
                                {artists.map(artist => (
                                  <SelectItem key={artist.id} value={artist.id}>
                                    {artist.displayName}
                                    {artist.location?.city && ` (${artist.location.city})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                            <Ruler className="w-3 h-3" />
                            Minimum Size (optional)
                          </Label>
                          <Input
                            value={flash.minSize}
                            onChange={(e) => updateFlash(flash.id, { minSize: e.target.value })}
                            placeholder="e.g., 5cm (leave empty if unknown)"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Summary and Upload Button */}
              {flashes.length > 0 && (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Upload Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-primary">{flashes.length}</div>
                          <div className="text-xs text-muted-foreground">Total Flashes</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {flashes.filter(f => f.cropValidation?.analyzed && f.cropValidation?.isValid).length}
                          </div>
                          <div className="text-xs text-muted-foreground">Valid Crops</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{completedFlashes.length}</div>
                          <div className="text-xs text-muted-foreground">Complete</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-orange-600">{flashes.length - completedFlashes.length}</div>
                          <div className="text-xs text-muted-foreground">Incomplete</div>
                        </div>
                      </div>
                      
                      {!canUploadFlashes() && (
                        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-md space-y-1">
                          {!fotwWeek && (
                            <p className="text-sm text-orange-800">
                              ⚠️ Enter a FOTW week number
                            </p>
                          )}
                          {!minPrice && (
                            <p className="text-sm text-orange-800">
                              ⚠️ Enter the minimum price for all flashes
                            </p>
                          )}
                          {!allFlashesValid && (
                            <p className="text-sm text-orange-800">
                              ⚠️ All flash crops must be validated and meet quality standards
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Upload Button */}
                  <div className="flex justify-center">
                    <Button
                      size="lg"
                      onClick={handleUploadAllFlashes}
                      disabled={!canUploadFlashes() || uploading}
                      className="px-8"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Upload {flashes.length} Flash{flashes.length !== 1 ? 'es' : ''} to FOTW Week {fotwWeek || '?'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminAuthGuard>
  )
}

